import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { motion } from "framer-motion";
import {
  deleteDonation,
  getDonationEntries,
  createAuditLog,
} from "../../utils/network/Crud";
import styles from "./AllDonations.module.scss";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import Loader from "../../Components/ProgressBar/Loader";
import { exportToExcel, getRecordChanges } from "../../utils/helper";
import { buildRecordsPDF, sharePDF } from "../../utils/pdfReport";
import { FaFileExport, FaRegEye } from "react-icons/fa";
import { AiOutlineDelete } from "react-icons/ai";
import { CiEdit } from "react-icons/ci";
import { toast } from "react-toastify";
import ConfirmationModal from "../../Components/ConfirmModal/ConfirmationModal";
import useTheme from "../../utils/useTheme";
import BackButton from "../../Components/BackButton";
import { debounce } from "lodash";
import ImagePreview from "./Component/ImagePreview";
import { SUPER_ADMIN_EMAIL } from "../../Auth";
import useTranslation from "../../utils/i18n/useTranslation";
import { GiReceiveMoney, GiPayMoney } from "react-icons/gi";
import {
  MdOutlinePendingActions,
  MdOutlineListAlt,
  MdOutlinePictureAsPdf,
  MdOutlineLocationOn,
  MdOutlineCalendarToday,
  MdOutlinePersonOutline,
  MdOutlineReceiptLong,
} from "react-icons/md";
import { toneFor, initialOf } from "../../utils/avatar";

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.4, ease: "easeOut" } },
};

const staggerContainer = {
  hidden: {},
  show: { transition: { staggerChildren: 0.06 } },
};

const AllDonations = ({ collection }) => {
  const [donationData, setDonationData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [initialLoading, setInitialLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState("");
  const [showPendings, setShowPendings] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState({
    flag: false,
    buferData: "",
  });
  const [isPreviewOpen, setIsPreviewOpen] = useState({
    flag: false,
    buferData: "",
  });
  const navigate = useNavigate();
  const { toggleGlobalLoading, globalLoading, userData, currentYear } =
    useTheme();
  const { t } = useTranslation();
  const isSuperAdmin = userData?.email === SUPER_ADMIN_EMAIL;
  const canEditYear = isSuperAdmin || currentYear === new Date().getFullYear();

  // Debounced Fetch Data
  const fetchData = useCallback(async () => {
    try {
      const data = await getDonationEntries(
        collection,
        searchTerm,
        true,
        selectedDate,
        currentYear
      );
      if (data) {
        setDonationData(data?.data);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setInitialLoading(false);
    }
  }, [collection, searchTerm, selectedDate, currentYear]);

  // Stable debounced fn: it used to be recreated whenever the theme context
  // re-rendered (toggleGlobalLoading is a new function each time), which
  // re-fired the fetch and remounted the list mid-click.
  const fetchDataRef = useRef(fetchData);
  fetchDataRef.current = fetchData;
  const debouncedFetchData = useMemo(
    () => debounce(() => fetchDataRef.current(), 1000),
    []
  );
  useEffect(() => () => debouncedFetchData.cancel(), [debouncedFetchData]);
  useEffect(() => {
    debouncedFetchData();
  }, [collection, searchTerm, selectedDate, currentYear, debouncedFetchData]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
  };

  const handleEdit = (data) => {
    navigate(
      collection === "expense"
        ? "/expenses/editExpense"
        : "/donations/editDonation",
      { state: data }
    );
  };

  const handleDelete = (item) => {
    setIsModalOpen({
      flag: true,
      buferData: item,
    });
  };

  const handleConfirmDelete = async () => {
    const deletedItem = isModalOpen.buferData;
    try {
      toggleGlobalLoading(true);
      const result = await deleteDonation(collection, deletedItem?.id);
      if (result) {
        toast.success("Deleted Successfully", { autoClose: 1200 });
        const trackedFields =
          collection === "donation"
            ? ["name", "address", "amount", "date", "isPending"]
            : ["description", "spendBy", "amount", "date", "isPending"];
        createAuditLog({
          collectionName: collection,
          entryId: deletedItem?.id,
          entryLabel:
            collection === "donation"
              ? deletedItem?.name
              : deletedItem?.description,
          action: "deleted",
          changes: getRecordChanges(deletedItem, {}, trackedFields),
          changedByName: userData?.name,
          changedByEmail: userData?.email,
        });
        debouncedFetchData(); // Refetch data after deletion
      }
    } catch (error) {
      console.error("Error deleting donation:", error);
    } finally {
      toggleGlobalLoading(false);
      setIsModalOpen({ flag: false, buferData: "" });
    }
  };

  const formattedDate = (item) =>
    item?.date?.seconds
      ? moment.unix(item?.date?.seconds).format("DD-MM-YYYY")
      : "No Date";

  const checkPermission = (action, key) => {
    return userData?.permissions?.[key]?.[action] || false;
  };

  const handleView = (item) => {
    setIsPreviewOpen({ flag: true, buferData: item?.images });
  };
  const RenderItem = ({ item }) => {
    const isExpense = collection === "expense";
    const title = isExpense ? item?.description : item?.name;
    const [toneA, toneB] = toneFor(title);
    const pending = !!item?.isPending;
    const statusText = pending
      ? isExpense
        ? t("paymentPending")
        : t("statusPending")
      : isExpense
      ? t("paymentDone")
      : t("collected");
    const address =
      item?.address?.length > 50
        ? `${item.address.slice(0, 50)}...`
        : item?.address;

    return (
      <motion.li
        key={item.id}
        className={`${styles.entryCard} ${pending ? styles.pending : ""}`}
        variants={fadeUp}
        whileHover={{ y: -2 }}
        style={{ "--tone-a": toneA, "--tone-b": toneB }}
      >
        <div className={styles.entryTop}>
          <span className={styles.entryAvatar} aria-hidden="true">
            {isExpense ? <MdOutlineReceiptLong /> : initialOf(title)}
          </span>
          <div className={styles.entryHeading}>
            <h3 className={styles.entryTitle}>{title || "—"}</h3>
            <div className={styles.entryMeta}>
              {!isExpense && address && (
                <span className={styles.metaChip} title={t("addressLabel")}>
                  <MdOutlineLocationOn />
                  {address}
                </span>
              )}
              {isExpense && item?.spendBy && (
                <span className={styles.metaChip} title={t("spentByLabel")}>
                  <MdOutlinePersonOutline />
                  {item.spendBy}
                </span>
              )}
              <span className={styles.metaChip} title={t("dateLabel")}>
                <MdOutlineCalendarToday />
                {formattedDate(item)}
              </span>
            </div>
          </div>
          <span
            className={`${styles.statusPill} ${
              pending ? styles.pendingPill : ""
            }`}
          >
            {statusText}
          </span>
        </div>
        <div className={styles.entryBottom}>
          <span className={styles.entryAmount}>
            ₹{(Number(item?.amount) || 0).toLocaleString()}
          </span>
          <div className={styles.entryActions}>
            {isExpense && item?.images?.length ? (
              <button
                type="button"
                className={`${styles.iconBtn} ${styles.view}`}
                onClick={() => handleView(item)}
                aria-label="View receipts"
              >
                <FaRegEye />
              </button>
            ) : null}
            {canEditYear && checkPermission("update", "donation") && (
              <button
                type="button"
                className={`${styles.iconBtn} ${styles.edit}`}
                onClick={() => handleEdit(item)}
                aria-label="Edit"
              >
                <CiEdit />
              </button>
            )}
            {canEditYear && checkPermission("delete", "donation") && (
              <button
                type="button"
                className={`${styles.iconBtn} ${styles.delete}`}
                onClick={() => handleDelete(item)}
                aria-label="Delete"
              >
                <AiOutlineDelete />
              </button>
            )}
          </div>
        </div>
      </motion.li>
    );
  };

  const handleExport = async () => {
    toggleGlobalLoading(true);
    try {
      const data = await getDonationEntries(
        collection,
        searchTerm,
        true,
        selectedDate,
        currentYear
      );
      if (data) {
        let finalPayload = {};
        if (collection === "expense") {
          finalPayload = data.data.map((elem) => ({
            Description: elem?.description,
            Amount: elem?.amount,
            Spent_By: elem?.spendBy,
            Date: moment.unix(elem?.date?.seconds).format("DD-MM-YYYY"),
          }));
        } else {
          finalPayload = data.data.map((elem) => ({
            Name: elem?.name,
            Amount: elem?.amount,
            Address: elem?.address,
            Date: moment.unix(elem?.date?.seconds).format("DD-MM-YYYY"),
            Pending: elem?.isPending,
          }));
        }
        const totalRow = {
          name: "Total",
          amount: finalPayload.reduce(
            (acc, curr) => (!curr.Pending ? acc + (curr.Amount || 0) : acc),
            0
          ),
        }; // Total row
        exportToExcel(
          finalPayload,
          `${collection === "expense" ? "Expense.xlsx" : "Donations.xlsx"}`,
          totalRow
        );
      }
    } catch (error) {
      console.error("Error exporting data:", error);
    } finally {
      toggleGlobalLoading(false);
    }
  };

  const handleSharePDF = async () => {
    toggleGlobalLoading(true);
    try {
      const data = await getDonationEntries(
        collection,
        searchTerm,
        true,
        selectedDate,
        currentYear
      );
      const records = data?.data || [];
      const filterParts = [];
      if (searchTerm) filterParts.push(`Search: "${searchTerm}"`);
      if (selectedDate) filterParts.push(`Date: ${selectedDate}`);
      if (showPendings) filterParts.push("Pending only");
      if (currentYear) filterParts.push(`Year: ${currentYear}`);

      const doc = buildRecordsPDF({
        collection,
        records: showPendings
          ? records.filter((r) => r.isPending)
          : records,
        filterLabel: filterParts.join(" | "),
      });
      const fileName = `${
        collection === "expense" ? "Expenses" : "Donations"
      }_${moment().format("YYYY-MM-DD")}.pdf`;
      await sharePDF(doc, fileName);
    } catch (error) {
      console.error("Error sharing PDF:", error);
      toast.error("Something went wrong generating the PDF!", {
        autoClose: 1200,
      });
    } finally {
      toggleGlobalLoading(false);
    }
  };

  if (initialLoading || globalLoading) return <Loader />;
  const mainData = showPendings
    ? donationData.filter((elem) => elem.isPending)
    : donationData;

  const totalAmount = donationData.reduce(
    (acc, elem) => (!elem?.isPending ? acc + (Number(elem?.amount) || 0) : acc),
    0
  );
  const pendingCount = donationData.filter((elem) => elem?.isPending).length;

  return (
    <>
      <BackButton
        title={collection === "donation" ? t("allDonations") : t("allExpenses")}
        renderToolbar={
          <>
            {userData?.email === SUPER_ADMIN_EMAIL && (
              <button
                className={styles.exportBtn}
                onClick={() => navigate(`/activity?type=${collection}`)}
              >
                {t("updateActivity")}
              </button>
            )}
            {canEditYear &&
              checkPermission(
                "create",
                collection === "donation" ? "donation" : "expense"
              ) && (
                <button
                  className={styles.addBtn}
                  onClick={() =>
                    navigate(
                      collection === "expense"
                        ? "/expenses/addExpense"
                        : "/donations/addDonation"
                    )
                  }
                >
                  {t("addNew")}
                </button>
              )}
          </>
        }
      />
      <div className={styles.container}>
        <motion.div
          className={styles.statsGrid}
          initial="hidden"
          animate="show"
          variants={staggerContainer}
        >
          <motion.div className={styles.statCard} variants={fadeUp}>
            <div className={styles.statIcon}>
              {collection === "expense" ? <GiPayMoney /> : <GiReceiveMoney />}
            </div>
            <div>
              <h3>₹ {totalAmount.toLocaleString()}</h3>
              <p>{collection === "expense" ? t("totalSpent") : t("totalCollected")}</p>
            </div>
          </motion.div>
          <motion.div className={styles.statCard} variants={fadeUp}>
            <div className={styles.statIcon}>
              <MdOutlineListAlt />
            </div>
            <div>
              <h3>{donationData.length}</h3>
              <p>{t("totalEntries")}</p>
            </div>
          </motion.div>
          <motion.div className={styles.statCard} variants={fadeUp}>
            <div className={styles.statIcon}>
              <MdOutlinePendingActions />
            </div>
            <div>
              <h3>{pendingCount}</h3>
              <p>{t("pendingLabel")}</p>
            </div>
          </motion.div>
        </motion.div>

        <div className={styles.panel}>
          <div className={`${styles.headerContainer}`}>
            <div className={styles.pendingCont}>
              <input
                value={showPendings}
                onChange={() => setShowPendings(!showPendings)}
                type="checkbox"
              />
              <span>{t("filterPendings")}</span>
            </div>
            <div className={styles.headerActions}>
              <button className={styles.exportBtn} onClick={handleExport}>
                {t("export")} <FaFileExport className={styles.icon} />
              </button>
              <button className={styles.exportBtn} onClick={handleSharePDF}>
                {t("sharePdf")} <MdOutlinePictureAsPdf className={styles.icon} />
              </button>
            </div>
          </div>
          <div className={styles.inputContainer}>
            <input
              className={styles.searchInput}
              type="text"
              placeholder={t("searchPlaceholder")}
              value={searchTerm}
              onChange={handleSearch}
            />
            <input
              className={styles.dateInput}
              type="date"
              value={selectedDate || ""}
              onChange={handleDateChange}
            />
          </div>
          {mainData?.length ? (
            <motion.ul
              className={styles.entryList}
              initial="hidden"
              animate="show"
              variants={staggerContainer}
            >
              {mainData?.map((elem) => RenderItem({ item: elem }))}
            </motion.ul>
          ) : (
            <>
              {!initialLoading && (
                <div className={styles.emptyList}>{t("listEmpty")}</div>
              )}
            </>
          )}
        </div>
        <ConfirmationModal
          isOpen={isModalOpen.flag}
          onConfirm={handleConfirmDelete}
          onCancel={() => setIsModalOpen({ flag: false, buferData: "" })}
        />
        <ImagePreview
          isOpen={isPreviewOpen?.flag}
          images={isPreviewOpen?.buferData}
          onClose={() => setIsPreviewOpen({ bufferData: "", flag: false })}
        />
      </div>
    </>
  );
};

export default AllDonations;
